import { useEffect, useState } from "react";
import { type ArtifactVariant, fetchArtifactFile, putArtifactThumbnail } from "@/api/artifacts";

/**
 * Local blob cache for artifact media. Artifact files are immutable, so once fetched they live in
 * Cache Storage forever (shared with `public/sw.js`) and reopened canvases / the library render
 * offline with zero network. Thumbnails are generated here on first view when the server has
 * none (Workers can't decode images) and uploaded so other devices get them too.
 */
export const ARTIFACT_CACHE = "gridcast-artifacts-v1";
const THUMB_MAX = 480;
const MAX_URLS = 300;

type CachedArtifact = { id: string; kind: string; thumbnailR2Key?: string | null };
type Entry = { url: string; refs: number };

const cacheKey = (artifactId: string, variant: ArtifactVariant) =>
	`https://artifacts.gridcast.local/${artifactId}/${variant}`;

const urls = new Map<string, Entry>();
const loading = new Map<string, Promise<string>>();
const thumbnailUploads = new Set<string>();

const openCache = () =>
	"caches" in globalThis ? caches.open(ARTIFACT_CACHE) : Promise.resolve(null);

async function readCache(artifactId: string, variant: ArtifactVariant) {
	const cache = await openCache();
	const hit = await cache?.match(cacheKey(artifactId, variant));
	return hit ? hit.blob() : null;
}

async function writeCache(artifactId: string, variant: ArtifactVariant, blob: Blob) {
	const cache = await openCache();
	await cache?.put(
		cacheKey(artifactId, variant),
		new Response(blob, { headers: { "Content-Type": blob.type } }),
	);
}

async function makeThumbnail(blob: Blob): Promise<Blob | null> {
	if (typeof OffscreenCanvas === "undefined" || !("createImageBitmap" in globalThis)) return null;
	const bitmap = await createImageBitmap(blob);
	const scale = Math.min(1, THUMB_MAX / Math.max(bitmap.width, bitmap.height));
	const canvas = new OffscreenCanvas(
		Math.max(1, Math.round(bitmap.width * scale)),
		Math.max(1, Math.round(bitmap.height * scale)),
	);
	canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
	bitmap.close();
	const webp = await canvas.convertToBlob({ type: "image/webp", quality: 0.82 });
	return webp.type === "image/webp"
		? webp
		: canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });
}

/** First-second frame of a video as an image blob (poster + library thumbnail). */
async function makeVideoPoster(blob: Blob): Promise<Blob | null> {
	const url = URL.createObjectURL(blob);
	try {
		const video = document.createElement("video");
		video.muted = true;
		video.playsInline = true;
		video.preload = "auto";
		video.src = url;
		await new Promise<void>((resolve, reject) => {
			video.onloadeddata = () => resolve();
			video.onerror = () => reject(new Error("Video could not be decoded"));
		});
		await new Promise<void>((resolve) => {
			video.onseeked = () => resolve();
			video.currentTime = Math.min(1, (video.duration || 0) / 3);
		});
		const bitmap = await createImageBitmap(video);
		const still = await new Promise<Blob | null>((resolve) => {
			const canvas = document.createElement("canvas");
			canvas.width = bitmap.width;
			canvas.height = bitmap.height;
			canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
			bitmap.close();
			canvas.toBlob(resolve, "image/png");
		});
		return still ? makeThumbnail(still) : null;
	} finally {
		URL.revokeObjectURL(url);
	}
}

async function loadBlob(artifact: CachedArtifact, variant: ArtifactVariant): Promise<Blob> {
	const cached = await readCache(artifact.id, variant);
	if (cached) return cached;
	const media = artifact.kind === "image" || artifact.kind === "video";
	if (variant === "thumb" && media && !artifact.thumbnailR2Key) {
		const original = await loadBlob(artifact, "original");
		const thumb = await (artifact.kind === "video"
			? makeVideoPoster(original)
			: makeThumbnail(original)
		).catch(() => null);
		if (!thumb) {
			if (artifact.kind === "video") throw new Error("No poster");
			return original;
		}
		await writeCache(artifact.id, "thumb", thumb);
		if (!thumbnailUploads.has(artifact.id)) {
			thumbnailUploads.add(artifact.id);
			void putArtifactThumbnail(artifact.id, thumb).catch(() => {});
		}
		return thumb;
	}
	const res = await fetchArtifactFile(artifact.id, variant);
	const blob = await res.blob();
	if ((res.headers.get("X-Artifact-Variant") ?? variant) === variant) {
		await writeCache(artifact.id, variant, blob);
	}
	return blob;
}

function evict() {
	if (urls.size <= MAX_URLS) return;
	for (const [key, entry] of urls) {
		if (urls.size <= MAX_URLS) break;
		if (entry.refs > 0) continue;
		URL.revokeObjectURL(entry.url);
		urls.delete(key);
	}
}

/** Object URL for an artifact's media, memoized across components. */
export function getArtifactUrl(
	artifact: CachedArtifact,
	variant: ArtifactVariant,
): Promise<string> {
	const key = `${artifact.id}:${variant}`;
	const known = urls.get(key);
	if (known) return Promise.resolve(known.url);
	let pending = loading.get(key);
	if (!pending) {
		pending = loadBlob(artifact, variant)
			.then((blob) => {
				const url = URL.createObjectURL(blob);
				urls.set(key, { url, refs: 0 });
				evict();
				return url;
			})
			.finally(() => loading.delete(key));
		loading.set(key, pending);
	}
	return pending;
}

export const peekArtifactUrl = (artifactId: string, variant: ArtifactVariant) =>
	urls.get(`${artifactId}:${variant}`)?.url;

/** Renders cached media synchronously when already in memory; otherwise loads once. */
export function useArtifactUrl(
	artifact: CachedArtifact | undefined | null,
	variant: ArtifactVariant,
) {
	const key = artifact ? `${artifact.id}:${variant}` : null;
	const [state, setState] = useState<{ key: string | null; url?: string; error?: boolean }>(() => ({
		key,
		url: artifact ? peekArtifactUrl(artifact.id, variant) : undefined,
	}));
	const id = artifact?.id;
	const kind = artifact?.kind;
	const thumbnailR2Key = artifact?.thumbnailR2Key;

	useEffect(() => {
		if (!id || !kind || !key) return;
		let active = true;
		const known = urls.get(key);
		if (known) {
			known.refs++;
			setState({ key, url: known.url });
		} else {
			getArtifactUrl({ id, kind, thumbnailR2Key }, variant)
				.then((url) => {
					const entry = urls.get(key);
					if (entry) entry.refs++;
					if (active) setState({ key, url });
					else if (entry) entry.refs--;
				})
				.catch(() => active && setState({ key, error: true }));
		}
		return () => {
			active = false;
			const entry = urls.get(key);
			if (entry && entry.refs > 0) entry.refs--;
		};
	}, [id, kind, thumbnailR2Key, key, variant]);

	return state.key === key
		? { url: state.url, error: !!state.error }
		: { url: undefined, error: false };
}

export async function downloadArtifactFile(
	artifact: CachedArtifact & { title: string; mimeType: string | null },
) {
	const url = await getArtifactUrl(artifact, "original");
	const ext = artifact.mimeType?.split("/")[1]?.split(/[+;]/)[0] ?? "bin";
	const a = document.createElement("a");
	a.href = url;
	a.download = `${(artifact.title || `artifact-${artifact.id.slice(-6)}`).replace(/[^\w.-]+/g, "_")}.${ext}`;
	a.click();
}

export async function forgetArtifact(artifactId: string) {
	const cache = await openCache();
	await Promise.all(
		(["original", "thumb", "meta"] as const).map((variant) => {
			const key = `${artifactId}:${variant}`;
			const entry = urls.get(key);
			if (entry) URL.revokeObjectURL(entry.url);
			urls.delete(key);
			return cache?.delete(cacheKey(artifactId, variant));
		}),
	);
}

let registered = false;

/** Production only: precache hashed chunks + the shell, and serve cached artifact media offline. */
export function registerServiceWorker() {
	if (registered || !import.meta.env.PROD || !("serviceWorker" in navigator)) return;
	registered = true;
	const register = () => void navigator.serviceWorker.register("/sw.js").catch(() => {});
	if (document.readyState === "complete") register();
	else window.addEventListener("load", register, { once: true });
}
